import { LeagueTableUserEvents } from "~/modules/analytics/events/leagueTable";
import { getManagerURL } from "~/routes";
import { RWA_COLORS } from "~/styles/colors";
import { getAssetClassLinkMetadata } from "~/utils/getAssetClassLinkMetadata";
import type {
    PerformanceChartSeries,
    PerformanceGroup,
} from "./getPerformanceChartData";
import type { ScreenerAsset } from "./types";

// // // //

export interface ReturnRow {
    analyticsLabel: string | null;
    color: string;
    href: string | null;
    id: number;
    name: string;
    oneMonth: number | null;
    threeMonth: number | null;
    yearToDate: number | null;
    oneYear: number | null;
}

function average(values: Array<number | null>): number | null {
    const availableValues = values.filter(
        (value): value is number => value !== null,
    );

    if (availableValues.length === 0) {
        return null;
    }

    return (
        availableValues.reduce((sum, value) => sum + value, 0) /
        availableValues.length
    );
}

export function getReturnRows(
    assets: ScreenerAsset[],
    group: PerformanceGroup,
    managerSlugs: Record<number, string> | null,
): ReturnRow[] {
    const groupedAssets = new Map<
        number,
        {
            assets: ScreenerAsset[];
            color: string;
            id: number;
            name: string;
            slug: string | null;
        }
    >();

    for (const asset of assets) {
        const metadata =
            group === "assetClass"
                ? {
                      color:
                          asset.assetClass.color_hex ?? RWA_COLORS.slate["500"],
                      id: asset.assetClass.id,
                      name: asset.assetClass.name,
                      slug: null,
                  }
                : asset.performanceManager
                  ? {
                        color:
                            asset.performanceManager.color_hex ??
                            RWA_COLORS.slate["500"],
                        id: asset.performanceManager.id,
                        name: asset.performanceManager.name,
                        slug: asset.performanceManager.slug,
                    }
                  : null;

        if (!metadata || metadata.id <= 0) {
            continue;
        }

        const existing = groupedAssets.get(metadata.id);
        if (existing) {
            existing.assets.push(asset);
        } else {
            groupedAssets.set(metadata.id, { ...metadata, assets: [asset] });
        }
    }

    return Array.from(groupedAssets.values()).map((groupData) => {
        const assetClassHref =
            group === "assetClass"
                ? getAssetClassLinkMetadata(groupData.id).href
                : null;
        const managerSlug =
            managerSlugs?.[groupData.id] ?? groupData.slug ?? undefined;

        return {
            analyticsLabel:
                group === "assetClass"
                    ? LeagueTableUserEvents.leagueTableAssetClassLinkClick
                    : managerSlug
                      ? LeagueTableUserEvents.leagueTableManagerLinkClick
                      : null,
            color: groupData.color,
            href:
                assetClassHref ??
                (managerSlug ? getManagerURL({ managerSlug }) : null),
            id: groupData.id,
            name: groupData.name,
            oneMonth: average(
                groupData.assets.map((asset) => asset.returns.trailing1Month),
            ),
            threeMonth: average(
                groupData.assets.map((asset) => asset.returns.trailing3Month),
            ),
            yearToDate: average(
                groupData.assets.map((asset) => asset.returns.yearToDate),
            ),
            oneYear: average(
                groupData.assets.map((asset) => asset.returns.trailing1Year),
            ),
        };
    });
}

export function alignReturnRowsWithChartSeries(
    returnRows: ReturnRow[],
    chartSeries: PerformanceChartSeries[],
): ReturnRow[] {
    const rowsByID = new Map(returnRows.map((row) => [row.id, row]));

    return chartSeries.map(
        (series): ReturnRow =>
            rowsByID.get(series.id) ?? {
                analyticsLabel: null,
                color: series.color,
                href: null,
                id: series.id,
                name: series.name,
                oneMonth: null,
                threeMonth: null,
                yearToDate: null,
                oneYear: null,
            },
    );
}
